import { forwardRef, useLayoutEffect, useRef, useCallback, useId, type ReactNode, type InputHTMLAttributes, type TextareaHTMLAttributes, type Ref } from 'react';
import clsx from 'clsx';
import { wrapTextChild } from '@/components/primitives/wrapTextChild';
import {
  inputRoot,
  inputField,
  inputElement,
  textareaElement,
  inputLabel,
  inputLabelError,
  inputRequired,
  inputAdornmentStart,
  inputAdornmentEnd,
  inputHelperText,
  inputHelperTextError,
} from './input.css';

export type InputSize = 'small' | 'medium';

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  /** Display label above the field */
  label?: string;
  /** Helper or error text below the field */
  helperText?: string;
  /** Error state — red border and error-colored helper text */
  error?: boolean;
  /** Size (default: small) */
  size?: InputSize;
  /** Leading adornment icon/element */
  startAdornment?: ReactNode;
  /** Trailing adornment icon/element */
  endAdornment?: ReactNode;
  /** Render as textarea */
  multiline?: boolean;
  /** Fixed row count for textarea */
  rows?: number;
  /** Minimum rows for auto-growing textarea */
  minRows?: number;
  /** Maximum rows for auto-growing textarea */
  maxRows?: number;
  /** Full width (default: true) */
  fullWidth?: boolean;
  /** Props forwarded to the inner <input>/<textarea> element */
  inputProps?: InputHTMLAttributes<HTMLInputElement> & TextareaHTMLAttributes<HTMLTextAreaElement>;
  /** Ref for the inner <input> or <textarea> element */
  inputRef?: Ref<HTMLInputElement> | Ref<HTMLTextAreaElement>;
}

const LINE_HEIGHT_PX = 21; // 14px font * 1.5 line-height

function calcMinHeight(rows: number) {
  // 2 × vertical padding (xs = 4px) + rows * line height
  return 2 * 4 + rows * LINE_HEIGHT_PX;
}

function calcMaxHeight(rows: number) {
  return 2 * 4 + rows * LINE_HEIGHT_PX;
}

export const Input = forwardRef<HTMLDivElement, InputProps>(function Input(
  {
    label,
    helperText,
    error = false,
    size = 'small',
    startAdornment,
    endAdornment,
    multiline = false,
    rows,
    minRows,
    maxRows,
    fullWidth = true,
    inputProps,
    inputRef,
    disabled = false,
    required = false,
    id,
    className,
    style,
    ...rest
  },
  ref
) {
  const autoId = useId();
  const resolvedId = id ?? (label ? autoId : undefined);
  const internalRef = useRef<HTMLTextAreaElement>(null);
  const resolvedRef = (inputRef as Ref<HTMLTextAreaElement>) ?? internalRef;

  // Auto-grow textarea when minRows/maxRows set
  const autoGrow = multiline && (minRows !== undefined || maxRows !== undefined) && rows === undefined;
  const handleAutoGrow = useCallback(() => {
    const el = internalRef.current ?? (typeof resolvedRef === 'object' && resolvedRef && 'current' in resolvedRef ? (resolvedRef as React.RefObject<HTMLTextAreaElement>).current : null);
    if (!el) return;
    el.style.height = 'auto';
    const newHeight = el.scrollHeight;
    const min = minRows ? calcMinHeight(minRows) : 0;
    const max = maxRows ? calcMaxHeight(maxRows) : Infinity;
    el.style.height = `${Math.min(Math.max(newHeight, min), max)}px`;
    el.style.overflowY = newHeight > max ? 'auto' : 'hidden';
  }, [resolvedRef, minRows, maxRows]);

  // Recalculate on externally-driven value changes (controlled inputs, parent state updates).
  // User typing is handled imperatively via onInput on the textarea below — that path avoids
  // any React render. useLayoutEffect (not useEffect) so the recalc runs before paint and
  // we don't flash the wrong height. The previous form had no deps array and ran on every
  // parent render of every multiline <Input>.
  const valueDep = (rest as { value?: unknown }).value;
  useLayoutEffect(() => {
    if (autoGrow) handleAutoGrow();
  }, [autoGrow, handleAutoGrow, valueDep]);

  const fieldClass = inputField({
    size,
    error: error || undefined,
    disabled: disabled || undefined,
    multiline: multiline || undefined,
  });

  const wrapperStyle: React.CSSProperties = {
    width: fullWidth ? '100%' : undefined,
    ...style,
  };

  const textareaStyle: React.CSSProperties = {};
  if (rows) {
    textareaStyle.minHeight = calcMinHeight(rows);
    textareaStyle.height = calcMinHeight(rows);
  } else if (minRows) {
    textareaStyle.minHeight = calcMinHeight(minRows);
  }
  if (maxRows && !rows) {
    textareaStyle.maxHeight = calcMaxHeight(maxRows);
  }

  return (
    <div ref={ref} className={clsx(inputRoot, className)} style={wrapperStyle}>
      {label && (
        <label
          htmlFor={resolvedId}
          className={clsx(inputLabel, error && inputLabelError)}
        >
          {wrapTextChild(label)}
          {required && <span className={inputRequired}>*</span>}
        </label>
      )}

      <div className={fieldClass}>
        {startAdornment && (
          <span className={inputAdornmentStart}>{startAdornment}</span>
        )}

        {multiline ? (
          <textarea
            ref={resolvedRef as Ref<HTMLTextAreaElement>}
            id={resolvedId}
            disabled={disabled}
            required={required}
            aria-invalid={error || undefined}
            className={textareaElement}
            style={textareaStyle}
            onInput={autoGrow ? handleAutoGrow : undefined}
            {...(inputProps as TextareaHTMLAttributes<HTMLTextAreaElement>)}
            {...(rest as unknown as TextareaHTMLAttributes<HTMLTextAreaElement>)}
          />
        ) : (
          <input
            ref={inputRef as Ref<HTMLInputElement>}
            id={resolvedId}
            disabled={disabled}
            required={required}
            aria-invalid={error || undefined}
            className={inputElement}
            {...inputProps}
            {...rest}
          />
        )}

        {endAdornment && (
          <span className={inputAdornmentEnd}>{endAdornment}</span>
        )}
      </div>

      {(helperText) && (
        <span className={clsx(inputHelperText, error && inputHelperTextError)}>
          {helperText}
        </span>
      )}
    </div>
  );
});
